import React from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'

import PermissionErrorPage from '../PermissionError'
import AgentPage from '../../components/templates/AgentPage'
import ProfileTable from '../../components/organisms/UserProfile/ProfileTable'
import DetailsPane from '../../components/organisms/ContactDetails/DetailsPane/index'
import LoadingSpinner from '../../components/atoms/LoadingSpinner'
import DetailsTopBar from '../../components/organisms/ContactDetails/DetailsTopBar'
import UserSummary from '../CustomerDetails/Mobile/UserSummary'
import CustomerProvider from '../../providers/CustomerProvider'
import MetaProvider from '../../providers/MetaProvider'
import EditProvider from '../../providers/EditProvider'
import useUserPermissions from '../../hooks/permissions/useUserPermissions'

import { Routes } from '../../Routes'
import { useUserAPI } from '../../providers/UserAPIProvider'
import { useBreakpoint } from '../../providers/BreakpointProvider'
import { fetchTypes } from '../../slices/userType'
import { selectRole } from '../../slices/role'
import { AddSuccessAlert } from '../../slices/alert'
import { clear } from '../../slices/avatarSlice'
import useStyles from './styles'

const UserProfile = () => {
  const { id } = useParams()
  const history = useHistory()
  const userTypes = useAppSelector(storeState => storeState.userType)
  const dispatch = useAppDispatch()

  const {
    allUsers,
    loadUser,
    loaded: usersLoaded,
    updateUser,
    uploadUser,
    userNotFound,
    setUserNotFound
  } = useUserAPI()
  const { checkCanEditUser, checkCanDeleteUser } = useUserPermissions()
  const roleSelector = useAppSelector(selectRole)
  const { adminRole } = roleSelector
  const { down770, up770 } = useBreakpoint()
  const { types } = userTypes

  const [img, _setImg] = React.useState(null)
  const [details, setDetails] = React.useState({})
  const [userFound, setUserFound] = React.useState(null)
  const [update, setUpdate] = React.useState(false)
  const [imageHasBeenEdited, setImageHasBeenEdited] = React.useState(false)

  const setImg = React.useCallback(
    newImg => {
      if (newImg === 'REMOVE') {
        _setImg(null)
        dispatch(clear(details?.avatar_id))
        setDetails(vals => ({ ...vals, avatar_id: null }))
        setImageHasBeenEdited(true)
      } else {
        _setImg(newImg)
      }
    },
    [dispatch, details]
  )

  const canEdit = React.useMemo(
    () => checkCanEditUser(details),
    [checkCanEditUser, details]
  )

  const canDelete = React.useMemo(
    () => checkCanDeleteUser(details),
    [checkCanDeleteUser, details]
  )

  const classes = useStyles({ smallMedia: down770 })

  React.useEffect(() => {
    if (userNotFound) {
      setUserNotFound(null)
    }
    //eslint-disable-next-line
  }, [id])

  // Get user details from cache
  React.useEffect(() => {
    if (!usersLoaded) {
      return
    }

    const user = allUsers[id]
    if (user) {
      setDetails(user)
      setUserFound(true)
    } else {
      loadUser(id)
    }
  }, [usersLoaded, id, userFound, allUsers, loadUser])

  // Get user details again if an update is needed
  React.useEffect(() => {
    if (update) {
      const user = allUsers[id]
      if (user) {
        setDetails(user)
        setUpdate(false)
      }
    }
  }, [update, allUsers, id])

  // saving uploaded image and updating provider with info
  React.useEffect(() => {
    if (img) {
      setDetails(vals => ({ ...vals, avatar_id: img }))
      setImageHasBeenEdited(true)
    }
  }, [setDetails, img])

  React.useEffect(() => {
    if (!userTypes.loaded && !userTypes.loading) {
      dispatch(fetchTypes())
    }
  }, [userTypes, dispatch])

  const redirectToCustomers = React.useCallback(
    id => {
      history.push(`${Routes.CUSTOMERS}/${id}`)
    },
    [history]
  )

  const sendData = React.useCallback(
    async (user, value, key) => {
      if (!user || !value || !types) {
        return
      }

      const copy = {
        ...user,
        [key]: value !== 'null' ? value : null
      }

      const typeObj = types.find(type => {
        if (type.id === value) {
          return type
        }
        return null
      })

      await uploadUser(
        copy,
        false,
        () => {
          // if type was changed, change url
          if (key === 'type_id' && typeObj.external) {
            redirectToCustomers(copy.id)
          }
        },
        typeObj
      )
    },
    [uploadUser, types, redirectToCustomers]
  )

  const handleTypeChange = React.useCallback(
    newType => {
      // updating api with change
      let updateDetails = { ...details }
      updateDetails = {
        ...details,
        type_id: newType
      }

      delete updateDetails.online_status
      if (newType !== types.find(type => type.name === 'User')?.id) {
        updateDetails.role_id = null
      } else {
        updateDetails.role_id = adminRole?.id ?? null
      }

      updateUser(updateDetails)
      sendData(updateDetails, newType, 'type_id')

      // updating state to reflect change
      setDetails(vals => ({
        ...vals,
        type_id: newType
      }))
    },
    [details, updateUser, types, sendData, adminRole]
  )

  const handleTimezoneChange = React.useCallback(
    newTimezone => {
      const updateDetails = { ...details, timezone: newTimezone }
      delete updateDetails.online_status

      updateUser(updateDetails)
      sendData(updateDetails, newTimezone, 'timezone')

      setDetails(vals => ({
        ...vals,
        timezone: newTimezone
      }))
      dispatch(AddSuccessAlert('Changes saved'))
    },
    [details, updateUser, sendData, dispatch]
  )

  return (
    <AgentPage scroll centerContent>
      <div className={classes.root}>
        {userNotFound === true ? (
          <PermissionErrorPage skipAgentPage />
        ) : !userFound ? (
          <LoadingSpinner />
        ) : (
          <MetaProvider user={details}>
            <EditProvider>
              <CustomerProvider user={details}>
                <DetailsTopBar
                  user={details}
                  img={img}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  setImg={setImg}
                  imageHasBeenEdited={imageHasBeenEdited}
                  setImageHasBeenEdited={setImageHasBeenEdited}
                  isTeam
                />

                <div className={classes.container}>
                  {down770 && (
                    <UserSummary
                      user={details}
                      setImg={setImg}
                      onTypeChange={handleTypeChange}
                      canEdit={canEdit}
                      isTeam
                    />
                  )}

                  {up770 && (
                    <DetailsPane
                      user={details}
                      setImg={setImg}
                      onTypeChange={handleTypeChange}
                      onTimezoneChange={handleTimezoneChange}
                      canEdit={canEdit}
                      update={() => setUpdate(true)}
                      updateUser={updateUser}
                      setDetails={setDetails}
                      isTeam
                    />
                  )}

                  <ProfileTable
                    details={details}
                    update={() => setUpdate(true)}
                    updateUser={updateUser}
                    img={img}
                    setDetails={setDetails}
                    canEdit={canEdit}
                  />
                </div>
              </CustomerProvider>
            </EditProvider>
          </MetaProvider>
        )}
      </div>
    </AgentPage>
  )
}

export default UserProfile
